// LT-460: inline-editable page title.
//
// Renders a heading that, when `editable`, exposes a pencil affordance;
// clicking it swaps the heading for an inline input that commits on
// Enter / the confirm button and cancels on Escape. Used by the campaign
// cockpit and the sequence builder to rename pre-launch / pre-publish.
import React, { useEffect, useRef, useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';

interface EditableTitleProps {
  value: string;
  /** When false the title is plain read-only text — no pencil. */
  editable: boolean;
  /** Persist the new name. Rejecting surfaces the error inline. */
  onSave: (next: string) => Promise<void>;
  /** Tailwind classes for the <h1> and the matching edit <input>. */
  className?: string;
  /** Extra classes for the edit <input> only (e.g. an explicit width so
   *  the field is comfortably sized regardless of the heading). */
  inputClassName?: string;
  maxLength?: number;
  ariaLabel?: string;
}

const EditableTitle: React.FC<EditableTitleProps> = ({
  value,
  editable,
  onSave,
  className = '',
  inputClassName = '',
  maxLength = 255,
  ariaLabel = 'Name',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  if (!editable || !editing) {
    return (
      <div className="flex items-center gap-2">
        <h1 className={className}>{value}</h1>
        {editable ? (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setDraft(value);
              setEditing(true);
            }}
            className="text-slate-400 hover:text-slate-100"
            aria-label={`Edit ${ariaLabel.toLowerCase()}`}
          >
            <Pencil size={16} strokeWidth={2} />
          </button>
        ) : null}
      </div>
    );
  }

  const commit = async () => {
    const next = draft.trim();
    if (!next) {
      setError(`${ariaLabel} is required.`);
      return;
    }
    if (next === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
    setDraft(value);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
          maxLength={maxLength}
          disabled={saving}
          aria-label={ariaLabel}
          className={`${className} ${inputClassName} bg-transparent border-b border-white/20 focus:border-accent focus:outline-none disabled:opacity-60`}
        />
        <button
          type="button"
          onClick={() => void commit()}
          disabled={saving}
          className="text-ok-text hover:opacity-80 disabled:opacity-50"
          aria-label="Save name"
        >
          <Check size={16} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="text-slate-400 hover:text-slate-100 disabled:opacity-50"
          aria-label="Cancel rename"
        >
          <X size={16} strokeWidth={2.5} />
        </button>
      </div>
      {error ? <span className="text-xs text-danger-text">{error}</span> : null}
    </div>
  );
};

export default EditableTitle;
